import type { AppMenuItem } from './menu-item';

export interface LocalSort {
  field: string;
  order: 1 | -1;
}

const comparable = (value: unknown) =>
  typeof value === 'string' ? value.toLowerCase() : value;

/**
 * Compares for sorting local data: texts without regard to case, empty values
 * first.
 */
export const compareValues = (a: unknown, b: unknown) => {
  const left = comparable(a);
  const right = comparable(b);
  if (left === right) {
    return 0;
  }
  if (left === null || left === undefined) {
    return -1;
  }
  if (right === null || right === undefined) {
    return 1;
  }
  return (left as number) < (right as number) ? -1 : 1;
};

/** A sorted copy of the rows; without sorting the rows as they are. */
export const sortRows = <T>(rows: T[] | null, sort: LocalSort | null): T[] => {
  if (!rows) {
    return [];
  }
  if (!sort) {
    return rows;
  }
  const { field, order } = sort;
  return rows
    .map((row, index) => ({ row, index }))
    .sort(
      (a, b) =>
        order *
          compareValues(
            (a.row as Record<string, unknown>)[field],
            (b.row as Record<string, unknown>)[field]
          ) || a.index - b.index
    )
    .map(({ row }) => row);
};

/**
 * The next sorting after a click on a column header: the first click sorts
 * ascending, the next ones toggle the order and Ctrl + click (Cmd + click)
 * removes the sorting.
 */
export const nextSort = (
  current: LocalSort | null,
  field: string,
  event?: Event
): LocalSort | null => {
  const { ctrlKey, metaKey } = (event || {}) as MouseEvent;
  if (ctrlKey || metaKey) {
    return null;
  }
  return {
    field,
    order: current && current.field === field ? (-current.order as 1 | -1) : 1,
  };
};

/** The sorting entries of the column header context menu. */
export const sortMenuItems = (
  field: string,
  apply: (sort: LocalSort | null) => void
): AppMenuItem[] => [
  {
    label: 'Sort Ascending',
    icon: 'app-icon fa-solid fa-arrow-up-short-wide',
    command: () => apply({ field, order: 1 }),
  },
  {
    label: 'Sort Descending',
    icon: 'app-icon fa-solid fa-arrow-down-wide-short',
    command: () => apply({ field, order: -1 }),
  },
  { label: 'Clear Sorting', command: () => apply(null) },
];
